import { selectAddress } from 'feature/addresses/redux/addressCreateSelector'
import { useErrorHandler } from 'feature/appeals/hooks/useErrorHandler'
import { useEditAppealMutation } from 'feature/appeals/redux/appeals.api'
import { ProductOrderHeader } from 'feature/productOrder/CreateOrder/ProductOrderHeader'
import { TSuggest } from 'feature/registration/types'
import { ScreenEnum, useScreen } from 'feature/screenMode'
import { ControlsType, MarkType } from 'feature/YandexMap/types'
import { YandexMap } from 'feature/YandexMap/YandexMap'
import { useSetNotice } from 'hooks/useSetNotice'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useActions, useAppSelector } from 'store/hooks'
import { Button } from 'ui'
import { LoaderInCentalPosition } from 'widget'
import { AddressSelectInput } from 'widget/AddressSelectInput'
import { ModalCloseButton } from 'widget/ModalCloseButton/ModalCloseButton'
import { Severity } from 'widget/Notifications'

import style from './ModalAddressEdit.module.scss'

type TModalAddressEdit = {
  location: TSuggest
  isModalOpen: boolean
  onClose: () => void
}

// форма редактирования адреса
export function ModalAddressEdit({
  location,
  onClose,
  isModalOpen,
}: TModalAddressEdit) {
  const methods = useForm({ defaultValues: { address: location.address } })
  const [isLoading, setLoading] = useState<boolean>()
  const { handleSubmit } = methods
  const screen = useScreen()
  const setNotice = useSetNotice()
  const errorHandler = useErrorHandler()

  const { isError, inputSuggest, input } = useAppSelector(selectAddress)

  const appeal = useAppSelector(state => state.newAppeal)
  const {
    setInputAddress,
    setIsError,
    setInputAddressSuggest,
    setClickMarkCoords,
    setCenter,
  } = useActions()
  const [editAppeal] = useEditAppealMutation()

  useEffect(() => {
    setInputAddress(location.address)
    setInputAddressSuggest(location)
    setIsError(false)
    setClickMarkCoords([Number(location.geoLat), Number(location.geoLon)])
    setCenter([Number(location.geoLat), Number(location.geoLon)])
  }, [])

  const onSubmit = () => {
    if (!inputSuggest) return
    setLoading(true)

    const newAppeal = {
      uid: appeal.uid,
      orderId: appeal.orderId,
      location: inputSuggest,
    }

    editAppeal(newAppeal)
      .unwrap()
      .then(res => {
        setLoading(false)
        if ('error' in res) {
          //@ts-ignore
          errorHandler(res.error.data)
          return
        }
        onClose()
        setNotice(res.message, Severity.success)
      })
      .catch(error => {
        setLoading(false)

        errorHandler(error.data)
      })
  }
  const handleClose = () => {
    onClose()
    setIsError(false)
  }

  const isSameAddress = input === location.address

  return (
    <FormProvider {...methods}>
      <form
        className={style.modalAddressEdit}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={style.headerWrapper}>
          <ProductOrderHeader
            title='Адрес объекта'
            description='Введите адрес, по которому планируете установку изделия.
            Можете ввести вручную или отметить место на карте.'
          />
          <div className={style.addressInputHeader}>
            <AddressSelectInput className={style.addressInput} />
          </div>

          <ModalCloseButton onClose={onClose} />
        </div>

        <div className={style.mapWrapper}>
          {isModalOpen && (
            <YandexMap
              className={style.map}
              isDadata
              marks={[MarkType.geolocationMark, MarkType.clickMark]}
              controls={[
                ControlsType.GeolocationControlUnit,
                ControlsType.ZoomControl,
              ]}
            />
          )}
        </div>

        <div className={style.addressInputFooter}>
          <AddressSelectInput className={style.addressInput} />
        </div>

        <div className={style.buttonWrapper}>
          <Button
            variant='contained'
            type='submit'
            disabled={isError || isSameAddress || isLoading}
            className={style.doneButton}
            data-testid='address-edit-submit'
          >
            {isLoading && <LoaderInCentalPosition />}
            Готово
          </Button>
          <Button
            variant={screen !== ScreenEnum.mobile ? 'outlined' : 'text'}
            onClick={handleClose}
            className={style.cancelBtn}
            data-testid='address-edit-cancel'
          >
            Отменить
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
